import React, { lazy, Suspense } from 'react';

import { ApolloProvider } from '@apollo/client';
import { Box, ThemeConstellation } from '@zillow/constellation';
import {
    getPageFrameData,
    PageFrame,
    PageFrameComponent,
} from '@zillow/page-frame-next/shopper-platform';
import { DeferredRender, RenderMode } from '@zillow/react-deferred-hydration';
import { ThemeProvider } from 'styled-components';

import AppUpsellBanner from './components/AppUpsellBanner/AppUpsellBanner';
import GlobalStyle from './components/GlobalStyle';
import HeadMetaTags from './components/HeadMetaTags';
import HeadScripts from './components/HeadScripts/HeadScripts';
import HeroContainer from './components/HeroComponents/HeroContainer';
import HopsSentry from './components/HopsSentry';
import RegionFooterLinks from './components/RegionFooterLinks/RegionFooterLinks';
import { graphqlClientId } from './constants/applicationConstants';
import { getInitState } from './contexts/getInitialServerState';
import { GlobalStoreStateProvider } from './contexts/GlobalStoreProvider';
import env from './env.factory';
import { createApolloClient } from './graphql/apolloUtils';

const BelowTheFold = lazy(() => import('./components/BelowTheFold/BelowTheFold'));

const client = (zgGraphHost, headers) =>
    createApolloClient({
        uri: zgGraphHost,
        requestHeaders: headers,
    });

const App = ({ pageFrameData, appData, headers }) => (
    <ThemeProvider theme={ThemeConstellation}>
        <GlobalStyle />
        <ApolloProvider client={client(env.zgGraphHost, headers)}>
            <HeadScripts />
            <HeadMetaTags />
            <PageFrame
                pageFrameData={pageFrameData}
                components={[
                    PageFrameComponent.HEADTAGS,
                    PageFrameComponent.TOPNAV,
                    PageFrameComponent.FOOTER,
                    PageFrameComponent.BODYSCRIPTS,
                    PageFrameComponent.REGLOGIN,
                ]}
            >
                <HopsSentry />
                <GlobalStoreStateProvider value={appData}>
                    <Box
                        sx={{
                            width: '2560px',
                            maxWidth: '100%',
                            minHeight: '1600px',
                            margin: '0 auto',
                        }}
                    >
                        <HeroContainer />
                        <DeferredRender mode={RenderMode.RENDER_CLIENT_SIDE_ON_IMMEDIATE}>
                            <Suspense fallback={<div></div>}>
                                <BelowTheFold />
                            </Suspense>
                            <AppUpsellBanner />
                        </DeferredRender>
                    </Box>
                    <RegionFooterLinks />
                </GlobalStoreStateProvider>
            </PageFrame>
        </ApolloProvider>
    </ThemeProvider>
);

App.getInitialProps = async ({ req, shopperPlatformConfig }) => {
    const { pfsHost, s3sHost } = shopperPlatformConfig;
    let pageFrameData;
    let appData = {};
    const pageFrameDataPromise = getPageFrameData(
        { req },
        {
            pfsHost,
            s3sHost,
            includeZsg: false,
            includeFonts: false,
            deferDropdowns: !shopperPlatformConfig.isBot,
            deferScripts: !shopperPlatformConfig.isBot,
            regionalizedLinks: true,
            staticMarkup: false,
            headerVersion: 'TRANSPARENT_MOBILE_ONLY',
        }
    );
    if (!shopperPlatformConfig.isBot) {
        const appDataPromise = getInitState(req, shopperPlatformConfig);
        pageFrameData = await pageFrameDataPromise;
        appData = await appDataPromise;
    } else {
        pageFrameData = await pageFrameDataPromise;
    }
    const headers = { ...req.headers, 'x-caller-id': graphqlClientId };

    return {
        pageFrameData,
        headers,
        pfsHost,
        s3sHost,
        appData,
        // See: https://zillow.pages.zgtools.net/shopper-platform-team/web-platform-documentation/docs/external/getting-started/server-side-rendering/#setting-body-class-during-ssr
        fullPage: true,
        bodyProps: { classes: ['znav-transparent'] },
    };
};

export default App;
